import { DocumentKind, documentKind } from './document-kind'
import { PAGE_ASPECT } from './document-stack-layout'

/**
 * Cómo se dibuja un documento en la grilla del cartapacio.
 *
 * ## Por qué es una función pura y no tres `if` en el componente
 *
 * La tarjeta toma TRES decisiones con la misma pinta de trivialidad: si va una foto o una pila de
 * hojas, qué imágenes apilar, y qué dice el renglón de abajo. Adentro del JSX, cada una queda
 * escrita al lado de un `<View>` y ninguna se puede probar. Acá se prueban las tres, incluidos los
 * casos que en la app aparecen una vez cada mil aperturas: `pages` en `null`, `thumbnailUrls`
 * vacío, `mimeType` ausente.
 */

export const DocumentCardType = {
  /** Se dibuja como una FOTO: un solo cuadro con borde. */
  image: 'image',
  /** Se dibuja como una PILA de hojas rotadas. */
  document: 'document'
} as const

export type DocumentCardType = (typeof DocumentCardType)[keyof typeof DocumentCardType]

export type DocumentCardSource = {
  mimeType?: string | null
  pages?: number | null
  thumbnailUrls?: string[] | null
  /** Ancho/alto de la miniatura, tal como lo publica el contrato. `null` = no se sabe. */
  thumbnailAspect?: number | null
}

export type DocumentCardView = {
  type: DocumentCardType
  /**
   * Las miniaturas a apilar, en orden. **Puede venir vacío**, y no es un error: hay documentos sin
   * miniatura —un PDF cifrado, un `.docx`, cualquier archivo anterior al modelo de assets— y en ese
   * caso la tarjeta dibuja el arte por tipo de `document-art`, que es el mismo que usan la fila y
   * la ficha.
   *
   * ⚠️ Las miniaturas se generan **dentro del request de subida**, no de forma asíncrona. Acá decía
   * lo contrario y era falso: el documento y sus previews nacen juntos, y si la generación falla el
   * documento se guarda igual, sin previews (`docs/architecture/documentos/miniaturas.md`).
   */
  images: string[]
  /**
   * La FORMA de la hoja, ancho/alto. Es la caja que la tarjeta reserva para cada página de la pila.
   *
   * Sale del contrato cuando el backend la publica (carta, A4, un plano apaisado) y cae a carta
   * cuando no —documentos sin miniatura, o con miniaturas anteriores a que se guardaran sus
   * medidas—. Hasta el 2026-09-10 era carta SIEMPRE y la hoja se recortaba contra ella con `cover`:
   * un A4 perdía los costados y un plano apaisado salía irreconocible.
   *
   * En una FOTO no se usa: la foto sigue llenando un cuadro, que es lo que dice la maqueta.
   */
  aspect: number
  /** El renglón de abajo: "Documento · 3 páginas", "Imagen". */
  description: string
}

/** Cuántas hojas se apilan como máximo. Más allá de tres el abanico se ve sucio, no lleno. */
const MAX_PAGES_IN_STACK = 3

export function documentCardView(document: DocumentCardSource): DocumentCardView {
  const isImage = documentKind(document.mimeType) === DocumentKind.image
  const thumbnails = document.thumbnailUrls ?? []

  return {
    type: isImage ? DocumentCardType.image : DocumentCardType.document,
    // Una foto es UNA imagen aunque el backend mande varias miniaturas: apilarlas sugeriría que
    // el archivo tiene páginas, y una foto no las tiene.
    images: isImage ? thumbnails.slice(0, 1) : thumbnails.slice(0, MAX_PAGES_IN_STACK),
    aspect: sheetAspect(document.thumbnailAspect),
    description: describe(isImage, document.pages)
  }
}

/**
 * El renglón de abajo de la tarjeta.
 *
 * Una imagen no dice páginas: un JPG «de 1 página» es una afirmación que nadie hizo. Un documento
 * SIN `pages` tampoco las inventa — dice sólo «Documento», que es lo que se sabe.
 */
function describe(isImage: boolean, pages: number | null | undefined): string {
  if (isImage) return 'Imagen'
  if (!pages || pages < 1) return 'Documento'

  return `Documento - ${pages} ${pages === 1 ? 'página' : 'páginas'}`
}

/**
 * La forma de la hoja que la tarjeta va a dibujar.
 *
 * Un aspecto ausente, cero, negativo o absurdo cae a carta. Los extremos se acotan a propósito: una
 * miniatura con un aspecto de 0,05 —una tira— dibujaría una hoja de un píxel de ancho dentro de la
 * celda, que se lee como un defecto de la app y no como el documento raro que es. El rango cubre de
 * sobra todo formato de papel (A4 vertical 0,71 · carta 0,77 · A3 apaisado 1,41) y deja margen para
 * un plano largo.
 */
function sheetAspect(aspect: number | null | undefined): number {
  if (typeof aspect !== 'number' || !Number.isFinite(aspect) || aspect <= 0) return PAGE_ASPECT
  return Math.min(Math.max(aspect, 0.3), 3)
}
